import { useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useChannel } from "@ui";
import { documentKeys, type Document } from "@agentic_system/api/document";

interface DocumentBroadcast {
  type: "document_updated" | "document_deleted";
  document?: Document;
  document_id?: number;
}

/**
 * Subscribe to real-time document updates (OCR status, embedding status changes)
 */
export function useDocumentsSocket() {
  const queryClient = useQueryClient();

  const handleReceived = useCallback(
    (data: DocumentBroadcast) => {
      if (data.type === "document_updated" && data.document) {
        // Update document in list cache
        queryClient.setQueriesData<{ documents: Document[]; meta: unknown }>(
          { queryKey: documentKeys.all },
          (old) => {
            if (!old?.documents) return old;
            return {
              ...old,
              documents: old.documents.map((d) =>
                d.id === data.document!.id ? data.document! : d,
              ),
            };
          },
        );

        // Update document detail cache if it exists
        queryClient.setQueryData<Document>(
          documentKeys.detail(data.document.id),
          (old) => (old ? { ...old, ...data.document } : old),
        );
      }

      if (data.type === "document_deleted" && data.document_id) {
        // Remove from list cache
        queryClient.setQueriesData<{ documents: Document[]; meta: unknown }>(
          { queryKey: documentKeys.all },
          (old) => {
            if (!old?.documents) return old;
            return {
              ...old,
              documents: old.documents.filter((d) => d.id !== data.document_id),
            };
          },
        );

        // Remove detail cache
        queryClient.removeQueries({
          queryKey: documentKeys.detail(data.document_id),
        });
      }
    },
    [queryClient],
  );

  useChannel<DocumentBroadcast>({
    params: { channel: "AgenticSystem::DocumentsChannel" },
    onReceived: handleReceived,
  });
}
